import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Map } from 'immutable';

import { fetchQueue } from 'flow/actions/pbx-actions';
import useAppContext from 'flow/hooks/useAppContext';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';

import { isQueueKey, StrategyState } from 'flow/utils';
import { generatePath } from 'flow/utils/router-utils';
import { getLoggedInMemberSize } from 'flow/utils/pbx-utils';

import FavoriteListItem from './contact-components/FavoriteListItem';
import SettingsListItem from './custom-components/SettingsListItem';
import Loading from 'flow/components/ui-components/Loading';
import MemberCountDescription from './pbx/MemberCountDescription';
import PbxWrittenMessagesSection from './pbx/PbxWrittenMessagesSection';
import Page from './Setting/Page';
import SettingSection from './Setting/SettingSection';
import Divider from './ui-components/Divider';
import RouteLink from './ui-components/RouteLink';

const PbxSettings = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { entityKey } = useParams<PbxParams>();

    const pbxDetails = useAppSelector((state) =>
        state.pbx.getIn(['queues', entityKey], Map()),
    );

    const queueMembers = useAppSelector((state) =>
        selectQueueMembers(state, entityKey),
    );

    useEffect(() => {
        dispatch(fetchQueue(entityKey));
    }, [dispatch, entityKey]);

    if (!pbxDetails?.get('queueCallStrategy') || !isQueueKey(entityKey)) {
        return <Loading />;
    }

    return (
        <Page
            header={i18next.t('pbx.settings')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['pbx.queues.entityKey'], { entityKey }),
                )
            }
        >
            <SettingsListItem
                editable={pbxDetails.get('editable')}
                description={pbxDetails.get('description')}
                to={generatePath(
                    routes['pbx.queues.entityKey.settings.edit.name'],
                    { entityKey },
                )}
            >
                {i18next.t('pbx.name')}
            </SettingsListItem>

            <Divider />

            <SettingSection>
                {i18next.t('Queue.Settings.CallerExperience')}
            </SettingSection>

            <SettingsListItem
                editable={pbxDetails.get('editable')}
                to={generatePath(
                    routes['pbx.queues.entityKey.settings.welcomemessage'],
                    { entityKey },
                )}
            >
                {i18next.t('Queue.Settings.List.WelcomeMessage')}
            </SettingsListItem>

            <SettingsListItem
                editable={pbxDetails.get('editable')}
                to={generatePath(
                    routes['pbx.queues.entityKey.settings.waitingsound'],
                    { entityKey },
                )}
                description={pbxDetails.getIn(['waitingSound', 'description'])}
            >
                {i18next.t('pbx.waitingsound')}
            </SettingsListItem>

            {pbxDetails.get('DTOSubtype') === 'OmniTicketChannelQueueDTO' &&
            pbxDetails.get('editable') ? (
                <PbxWrittenMessagesSection entityKey={entityKey as QueueKey} />
            ) : null}

            <Divider />

            <SettingSection>
                {i18next.t('Queue.Settings.OtherSettings')}
            </SettingSection>

            <RouteLink
                to={generatePath(
                    routes['pbx.queues.queueKey.settings.members'],
                    {
                        queueKey: entityKey,
                    },
                )}
                description={
                    <MemberCountDescription
                        loggedInMembers={getLoggedInMemberSize(queueMembers)}
                        totalMembers={queueMembers?.size || 0}
                    />
                }
            >
                {i18next.t('Queue.HandleMembers.Title')}
            </RouteLink>

            <SettingsListItem
                editable={pbxDetails.get('editable')}
                to={generatePath(
                    routes['pbx.queues.entityKey.settings.calendar'],
                    { entityKey },
                )}
            >
                {i18next.t('pbx.opening_hours')}
            </SettingsListItem>

            <SettingsListItem
                editable={pbxDetails.get('editable')}
                to={generatePath(
                    routes['pbx.queues.queueKey.settings.callstrategy'],
                    { queueKey: entityKey },
                )}
                description={i18next.t(
                    `Queue.CallStrategy.Item.${StrategyState[pbxDetails.get('queueCallStrategy')].value}`,
                )}
            >
                {i18next.t('Queue.Settings.List.CallStrategy')}
            </SettingsListItem>

            <RouteLink
                to={generatePath(
                    routes['pbx.queues.entityKey.settings.statistics'],
                    { entityKey },
                )}
            >
                {i18next.t('Queue.Statistics.Title')}
            </RouteLink>

            <FavoriteListItem />

            {pbxDetails.get('editable') ? (
                <RouteLink
                    to={generatePath(
                        routes['pbx.queues.entityKey.settings.tickets'],
                        { entityKey },
                    )}
                >
                    {i18next.t('route.tickets')}
                </RouteLink>
            ) : null}
        </Page>
    );
};

export default PbxSettings;
